

















































































































import axios from 'axios';
import { get, cloneDeep } from 'lodash';
import { User, Space, Image, Reaction } from '@/types/GlobalTypes.ts';
import EventBus from '@/eventBus.js';
import { createImageUploadFormData } from '@/utils/imageUpload';
import CoUser from '../../Molecules/co-user/CoUser.vue';
import CoLink from '../../Atoms/co-link/CoLink.vue';
import CoDropdown from '../../Molecules/co-dropdown/CoDropdown.vue';
import CoDropdownItem from '../../Molecules/co-dropdown-item/CoDropdownItem.vue';
import CoIcon from '../../Atoms/co-icon/CoIcon.vue';
import CoReactions from '../../Molecules/co-reactions/coReactions.vue';
import CoEditor from '../../Molecules/co-editor/CoEditor.vue';
import CoConfirmation from '../../Molecules/co-confirmation/CoConfirmation.vue';
import CoInput from '../../Molecules/co-input/CoInput.vue';
import CoSlider from '../../Molecules/co-slider/CoSlider.vue';
import CoImage from '../../Atoms/co-image/CoImage.vue';
import CoEditorContent from '../../Molecules/co-editor-content/CoEditorContent.vue';

interface Comment {
    ID: string;
    PostID: string;
    PostSlug: string;
    PostType: string;
    Author: User;
    Text: string;
    CreatedAt: number;
    UpdatedAt: number;
    Space: Space;
    Images: Image[];
    Reactions?: Record<string, unknown>;
}

const REALTIME_COMMENT_MUTABLE_FIELDS = ['Text', 'Images', 'Reactions', 'UpdatedAt'];

export default {
    components: {
        CoLink,
        CoDropdown,
        CoDropdownItem,
        CoIcon,
        CoReactions,
        CoEditor,
        CoInput,
        CoConfirmation,
        CoSlider,
        CoImage,
        CoUser,
        CoEditorContent,
    },
    name: 'CoComment',
    data() {
        return {
            editorShown: false,
            replyValue: null,
            deleted: false,
            sending: false,
            isAdmin: !!(
                get(this, '$store.state.me.Permissions') &&
                get(this, '$store.state.me.Permissions').includes('space_admin')
            ),
            isMySpace: get(this.comment, 'SpaceID') === get(this, '$store.state.space.ID'),
            rerender: 0,
            reactionsVersion: 0,
        };
    },
    props: {
        comment: {
            type: Object as () => Comment,
            required: true,
            default: () => ({}),
        },
    },
    computed: {
        commentLink() {
            let postType = this.comment.PostType || '';
            postType = postType.toLowerCase();
            // support links for legacy comments on new created items in feed
            switch (this.comment.PostType) {
                case 'project':
                case 'page':
                case 'post':
                case 'event':
                case 'project-update':
                case 'projectupdate':
                    postType = 'post';
                    break;
                case 'job':
                case 'market-item':
                    postType = 'market-item-as-post';
                    break;
                default:
                    postType = 'post';
                    break;
            }
            return `${window.location.origin}/${postType}/${this.comment.PostSlug}?commentid=${this.comment.ID}`;
        },
        reply() {
            return {
                Version: '1',
                PostID: this.comment.PostID,
                PostType: this.comment.PostType,
            };
        },
        canEdit() {
            if (!get(this, '$store.state.me.ID') && !get(this, 'comment.Author.ID')) return false;
            return get(this, '$store.state.me.ID') === get(this, 'comment.Author.ID');
        },
    },
    watch: {
        editorShown(newVal, oldVal) {
            if (!newVal) return;
            this.$nextTick(() => {
                this.$refs.replyEditor.focus();
            });
        },
        deleted(newVal, oldVal) {
            if (newVal === true) {
                this.$emit('commentDeleted', this.comment);
            }
        },
    },
    mounted() {
        EventBus.$on(`comment_modify_${this.comment.ID}`, this.handleRealtimeCommentModified);
    },
    beforeDestroy() {
        EventBus.$off(`comment_modify_${this.comment.ID}`, this.handleRealtimeCommentModified);
    },
    methods: {
        get, // lodash
        handleRealtimeCommentModified(updatedComment) {
            if (!updatedComment || String(updatedComment.ID || updatedComment.CommentID) !== String(this.comment.ID)) {
                return;
            }

            // Synchronize the displayed comment with the realtime payload, including a complete reactions replacement.
            /* eslint-disable vue/no-mutating-props -- Comment props are render-time copies owned by the comments list. */
            REALTIME_COMMENT_MUTABLE_FIELDS.forEach((field) => {
                if (!Object.prototype.hasOwnProperty.call(updatedComment, field)) return;
                this.$set(this.comment, field, cloneDeep(updatedComment[field]));

                if (field === 'Reactions') {
                    // CoReactions keeps an optimistic local copy, so remount it with the server-authoritative reactions.
                    this.reactionsVersion += 1;
                }
            });

            if (!Object.prototype.hasOwnProperty.call(updatedComment, 'Reactions')) {
                // The SSE serializer omits the reaction map after the final reaction is removed.
                this.$set(this.comment, 'Reactions', {});
                this.reactionsVersion += 1;
            }
            /* eslint-enable vue/no-mutating-props */
        },
        replyToggle() {
            this.editorShown = !this.editorShown;
            if (!this.editorShown) return;
            this.$nextTick(() => {
                if (!this.comment?.Author?.Slug || this.comment?.Author?.Status === -1) return; // handle unavailable author
                this.$refs.replyEditor.insertMention(
                    get(this.comment, 'Author.Name'),
                    get(this.comment, 'Author.Slug')
                );
            });
        },
        uploadImages(files) {
            if (!files || files.length === 0) return Promise.resolve([]);

            return Promise.all(
                files.map(async (file) => {
                    const data = await createImageUploadFormData(file.file);
                    const response = await axios({
                        method: 'POST',
                        url: '/upload/image/comment',
                        data,
                        withCredentials: true,
                    });

                    return get(response, 'data.url');
                })
            );
        },
        async createComment(commentData) {
            if (!commentData || typeof commentData !== 'object') return;
            this.sending = true;
            const images = await this.uploadImages(commentData.Files);
            const newComment = {
                ...this.reply,
                Text: get(commentData, 'Html', ''),
                Images: images,
            };

            const data = JSON.stringify(newComment);

            axios({
                method: 'POST',
                url: '/comment',
                data,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    // update the comments
                    this.$emit('replied', response.data);
                    // clear the editor
                    this.$refs.replyEditor.clear(true);
                    this.replyToggle();
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.sending = false;
                });
        },
        commentDelete(asAdmin = false) {
            axios({
                method: 'DELETE',
                url: asAdmin ? '/admin/comment' : '/comment',
                data: this.comment,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(async (response) => {
                    this.deleted = true;

                    // Wait for animation to complete
                    await new Promise((resolve) => setTimeout(resolve, 300)); // Match transition duration

                    this.$emit('commentDeleted', this.comment);
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        copyLink() {
            EventBus.$emit('INFO', {
                Message: this.$t('messages.copiedtoclipboard'),
            });
            navigator.clipboard.writeText(this.commentLink);
        },
    },
};
